// @ts-check
// Lista de bloqueio em Configurações > Contas conectadas (doc 03 do mapeamento, seção 6; contrato 08 de e-mail
// avançado, seção 2): a minha (vale para as minhas contas conectadas) e a da escola (vale para todos os canais de
// e-mail, só com a flag WORKSPACE). E-mails de quem está na lista não entram no CRM.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { addToBlocklist, getBlocklist, removeFromBlocklist } from '../../data/email.js';
import { formatRelativeToNow } from '../../lib/format.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { t } from '../../i18n/index.js';

/** @typedef {import('../../data/email.js').Blocklist} Blocklist */

/**
 * @param {{ items: import('../../data/email.js').BlocklistItem[], canEdit: boolean, onRemove: (itemId: string) => void }} props
 */
function BlocklistTable({ items, canEdit, onRemove }) {
  if (items.length === 0) return html`<p class="blocklist-empty">${t('emailAdvanced.blocklist.empty')}</p>`;
  return html`<div class="blocklist-table" role="table" aria-label=${t('emailAdvanced.blocklist.title')}>
    <div class="blocklist-row blocklist-header" role="row">
      <span role="columnheader">${t('emailAdvanced.blocklist.columns.handle')}</span>
      <span role="columnheader">${t('emailAdvanced.blocklist.columns.createdAt')}</span>
      <span role="columnheader"></span>
    </div>
    ${items.map((item) => html`<div class="blocklist-row" role="row" key=${item.id} data-testid="blocklist-row">
      <span class="blocklist-handle" role="cell"><${Icon} name=${item.handle.startsWith('@') ? 'IconWorld' : 'IconAt'} size=${14} />${item.handle}</span>
      <span class="blocklist-date" role="cell">${formatRelativeToNow(item.createdAt)}</span>
      <span role="cell">
        ${canEdit
          ? html`<${LightIconButton} icon="IconTrash" ariaLabel=${t('emailAdvanced.blocklist.remove', { handle: item.handle })} onClick=${() => onRemove(item.id)} />`
          : null}
      </span>
    </div>`)}
  </div>`;
}

export function BlocklistSection() {
  const [data, setData] = useState(/** @type {Blocklist | null} */ (null));
  const [scope, setScope] = useState(/** @type {'MEMBER' | 'WORKSPACE'} */ ('MEMBER'));
  const [input, setInput] = useState('');
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);

  const load = () => getBlocklist().then(setData).catch((loadError) => {
    showErrorSnackbar(loadError);
    setData({ member: [], workspace: [], canEditWorkspace: false });
  });
  useEffect(() => {
    load();
  }, []);

  const canEdit = scope === 'MEMBER' || Boolean(data?.canEditWorkspace);
  const items = data ? (scope === 'MEMBER' ? data.member : data.workspace) : [];

  const submit = async (/** @type {Event} */ event) => {
    event.preventDefault();
    if (!input.trim()) return;
    setIsSaving(true);
    setError(null);
    try {
      const result = await addToBlocklist(scope, input);
      if (result.errors.length > 0) {
        setError(result.errors.map((item) => t(`emailAdvanced.blocklist.errors.${item.code}`, { handle: item.handle })).join(' '));
        setInput(result.errors.map((item) => item.handle).join(', '));
      } else {
        setInput('');
      }
      if (result.added.length > 0) showSuccessSnackbar(t('emailAdvanced.blocklist.added', { count: result.added.length }));
      await load();
    } catch (requestError) {
      showErrorSnackbar(requestError);
    } finally {
      setIsSaving(false);
    }
  };

  const remove = async (/** @type {string} */ itemId) => {
    try {
      await removeFromBlocklist(itemId);
      showSuccessSnackbar(t('emailAdvanced.blocklist.removed'));
      await load();
    } catch (requestError) {
      showErrorSnackbar(requestError);
    }
  };

  return html`<div class="blocklist-section" data-testid="blocklist-section">
    <div class="segmented-control" role="tablist">
      ${/** @type {const} */ (['MEMBER', 'WORKSPACE']).map((option) => html`<button
        type="button" key=${option} role="tab" class="segmented-item" aria-selected=${scope === option ? 'true' : 'false'}
        data-active=${scope === option ? 'true' : undefined}
        onClick=${() => {
          setScope(option);
          setError(null);
        }}
      ><${Icon} name=${option === 'MEMBER' ? 'IconUser' : 'IconBuildingCommunity'} size=${14} />${t(`emailAdvanced.blocklist.scopes.${option}`)}</button>`)}
    </div>
    <p class="blocklist-scope-hint">${t(`emailAdvanced.blocklist.scopeHints.${scope}`)}</p>
    ${canEdit
      ? html`<form class="blocklist-form" onSubmit=${submit}>
          <input class="text-input" value=${input} placeholder=${t('emailAdvanced.blocklist.placeholder')} aria-label=${t('emailAdvanced.blocklist.placeholder')}
            onInput=${(event) => setInput(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
          <${Button} title=${t('emailAdvanced.blocklist.add')} icon="IconForbid" variant="secondary" size="small" type="submit" isLoading=${isSaving} />
        </form>`
      : html`<p class="channel-card-note"><${Icon} name="IconLock" size=${14} />${t('emailAdvanced.blocklist.workspaceReadOnly')}</p>`}
    ${error ? html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${error}</span></div>` : null}
    ${data === null ? html`<${Skeleton} height=${80} radius=${8} />` : html`<${BlocklistTable} items=${items} canEdit=${canEdit} onRemove=${remove} />`}
  </div>`;
}
